import { toast } from 'sonner';
import { useApi } from '@/hooks/use-api';
import FileUpload from '@/components/FileUpload';
import RichTextEditor from '@/components/lms/RichTextEditor';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { PHONE_OPTS, dialForIso, isoForDial } from '@/lib/countryCodes';

export const FUND_TYPES = ['Retail Investor', 'HNI', 'Angel Investor', 'Venture Capitalist (VC)', 'Private Equity (PE)', 'Corporate Venture Capital (CVC)', 'Institutional Investor', 'Incubator / Accelerator', 'Family Office', 'Wealth Fund'];
export const POC_ROLES = ['Partner', 'Managing Partner', 'General Partner', 'Principal', 'Vice President', 'Director', 'Investment Lead', 'Analyst', 'Founder / Solo GP', 'Other'];
export const CHECK_SIZES = ['₹10L – 50L', '₹50L – 1Cr', '₹1Cr – 5Cr', '₹5Cr – 10Cr', '₹10Cr – 25Cr', '₹25Cr – 50Cr', '₹50Cr+'];
export const STAGE_OPTIONS = ['Idea', 'Pre-seed', 'Seed', 'Series A', 'Series B', 'Growth'];
export const SECTOR_OPTIONS = ['B2B SaaS', 'Fintech', 'Consumer', 'D2C', 'AI/ML', 'Healthtech', 'Edtech', 'Climate', 'Agritech', 'Deeptech', 'Mobility', 'Marketplaces', 'Developer Tools', 'Commerce Infra', 'Sector Agnostic'];
export const STATE_CITIES = {
  Karnataka: ['Bengaluru', 'Mysuru', 'Mangaluru', 'Hubballi'],
  Maharashtra: ['Mumbai', 'Pune', 'Nagpur', 'Nashik'],
  Delhi: ['New Delhi', 'Delhi NCR'],
  Haryana: ['Gurugram', 'Faridabad'],
  'Uttar Pradesh': ['Noida', 'Lucknow', 'Kanpur'],
  Telangana: ['Hyderabad', 'Warangal'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai'],
  Kerala: ['Kochi', 'Thiruvananthapuram', 'Kozhikode'],
  Gujarat: ['Ahmedabad', 'Surat', 'Vadodara', 'GIFT City'],
  Rajasthan: ['Jaipur', 'Udaipur', 'Jodhpur'],
  'West Bengal': ['Kolkata', 'Siliguri'],
  Punjab: ['Chandigarh', 'Ludhiana', 'Amritsar'],
  'Madhya Pradesh': ['Indore', 'Bhopal'],
  Goa: ['Panaji', 'Margao'],
  'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Amaravati'],
  Odisha: ['Bhubaneswar', 'Cuttack'],
  Bihar: ['Patna'],
  Assam: ['Guwahati'],
  'Remote / Other': ['Remote', 'Other'],
};

const ChipGroup = ({ options, value = [], onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={testid}>
    {options.map((o) => {
      const active = value.includes(o);
      return (
        <button
          key={o}
          type="button"
          onClick={() => onChange(active ? value.filter((v) => v !== o) : [...value, o])}
          data-testid={`${testid}-${o.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
          className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${active ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:text-foreground bg-card'}`}
        >
          {o}
        </button>
      );
    })}
  </div>
);

export const InvestorForm = ({ form, setForm }) => {
  const api = useApi();
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));
  const setText = (k) => (e) => set(k)(e.target.value);
  const cities = STATE_CITIES[form.state] || [];
  // Legacy/free-form values saved before the presets existed must still preselect.
  const withCurrent = (opts, v) => (v && !opts.includes(v) ? [v, ...opts] : opts);

  const onLogoUploaded = async ({ key }) => {
    try { set('avatar')(await api.publicUrl(key)); toast.success('Logo uploaded'); }
    catch (e) { toast.error('Upload failed', { description: e.message }); }
  };

  return (
    <div className="grid gap-4">
      {/* Logo */}
      <div>
        <Label className="text-xs">Investor logo / avatar</Label>
        <div className="flex items-center gap-3 mt-1.5">
          {form.avatar ? (
            <img src={form.avatar} alt="" className="h-14 w-14 rounded-xl object-cover border border-border" data-testid="investor-avatar-preview" />
          ) : (
            <div className="h-14 w-14 rounded-xl bg-secondary grid place-items-center font-display text-lg border border-border">{(form.name || '?')[0]}</div>
          )}
          <div className="w-40"><FileUpload folder="directory/investors" accept="image/*" label="Upload logo" resetAfterUpload testid="investor-avatar-upload" onUploaded={onLogoUploaded} /></div>
          {form.avatar && <button type="button" className="text-xs text-muted-foreground underline hover:text-destructive" onClick={() => set('avatar')('')} data-testid="investor-avatar-remove">Remove</button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-4">
        <div>
          <Label className="text-xs">Investor / Fund name <span className="text-destructive">*</span></Label>
          <Input value={form.name || ''} onChange={setText('name')} placeholder="Blume Ventures" className="mt-1.5" data-testid="field-name" />
        </div>
        <div>
          <Label className="text-xs">Fund type</Label>
          <Select value={form.fund_type || ''} onValueChange={set('fund_type')}>
            <SelectTrigger className="mt-1.5" data-testid="field-fund_type"><SelectValue placeholder="Select fund type" /></SelectTrigger>
            <SelectContent>{withCurrent(FUND_TYPES, form.fund_type).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">POC name (point of contact)</Label>
          <Input value={form.poc_name || ''} onChange={setText('poc_name')} placeholder="Anika Rao" className="mt-1.5" data-testid="field-poc_name" />
        </div>
        <div>
          <Label className="text-xs">POC role</Label>
          <Select value={form.role || ''} onValueChange={set('role')}>
            <SelectTrigger className="mt-1.5" data-testid="field-role"><SelectValue placeholder="Select role" /></SelectTrigger>
            <SelectContent>{withCurrent(POC_ROLES, form.role).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" value={form.email || ''} onChange={setText('email')} placeholder="anika@blume.vc" className="mt-1.5" data-testid="field-email" />
        </div>
        <div>
          <Label className="text-xs">Phone</Label>
          <div className="flex gap-2 mt-1.5">
            <div className="w-[104px] shrink-0">
              <Combobox
                testid="field-country_code"
                options={PHONE_OPTS}
                value={isoForDial(form.country_code || '+91')}
                onChange={(iso) => set('country_code')(dialForIso(iso))}
                triggerClassName="w-[104px]"
              />
            </div>
            <Input value={form.phone || ''} onChange={setText('phone')} placeholder="98450 11223" className="flex-1" data-testid="field-phone" />
          </div>
        </div>
        <div>
          <Label className="text-xs">State</Label>
          <Select value={form.state || ''} onValueChange={(v) => setForm((p) => ({ ...p, state: v, city: '' }))}>
            <SelectTrigger className="mt-1.5" data-testid="field-state"><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent>{Object.keys(STATE_CITIES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">City</Label>
          <Select value={form.city || ''} onValueChange={set('city')} disabled={!form.state}>
            <SelectTrigger className="mt-1.5" data-testid="field-city"><SelectValue placeholder={form.state ? 'Select city' : 'Pick a state first'} /></SelectTrigger>
            <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Check size</Label>
          <Select value={form.check_size || ''} onValueChange={set('check_size')}>
            <SelectTrigger className="mt-1.5" data-testid="field-check_size"><SelectValue placeholder="Typical cheque range" /></SelectTrigger>
            <SelectContent>{withCurrent(CHECK_SIZES, form.check_size).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs">Stage focus — tap all that apply</Label>
        <ChipGroup options={STAGE_OPTIONS} value={form.stage_focus || []} onChange={set('stage_focus')} testid="field-stage_focus" />
      </div>
      <div>
        <Label className="text-xs">Sector focus — tap all that apply</Label>
        <ChipGroup options={SECTOR_OPTIONS} value={form.sector_focus || []} onChange={set('sector_focus')} testid="field-sector_focus" />
      </div>

      <div>
        <Label className="text-xs">Thesis — what do they look for?</Label>
        <div className="mt-1.5">
          <RichTextEditor value={form.thesis || ''} onChange={(html) => set('thesis')(html)} placeholder="India-first founders building global SaaS from day one…" testid="field-thesis" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-4">
        <div>
          <Label className="text-xs">LinkedIn URL</Label>
          <Input value={form.linkedin || ''} onChange={setText('linkedin')} placeholder="https://linkedin.com/in/…" className="mt-1.5" data-testid="field-linkedin" />
        </div>
        <div>
          <Label className="text-xs">Fund website</Label>
          <Input value={form.website || ''} onChange={setText('website')} placeholder="https://blume.vc" className="mt-1.5" data-testid="field-website" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Application form link <span className="text-muted-foreground">(optional)</span></Label>
        <Input value={form.apply_url || ''} onChange={setText('apply_url')} placeholder="https://forms.gle/… or the fund's pitch-submission page" className="mt-1.5" data-testid="field-apply_url" />
        <p className="text-[11px] text-muted-foreground mt-1">Founders see an "Apply" button on this investor's profile — it opens this form in a new tab and their application is logged.</p>
      </div>

      <div>
        <Label className="text-xs">Notable portfolio (comma separated)</Label>
        <Input
          value={Array.isArray(form.portfolio) ? form.portfolio.join(', ') : (form.portfolio || '')}
          onChange={(e) => set('portfolio')(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          placeholder="Dunzo, slice, Classplus"
          className="mt-1.5"
          data-testid="field-portfolio"
        />
      </div>

      <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-4 py-3">
        <div>
          <Label className="text-xs">Featured investor</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">Highlighted with a star and shown first on the student directory.</p>
        </div>
        <Switch checked={!!form.featured} onCheckedChange={set('featured')} data-testid="field-featured" />
      </div>
    </div>
  );
};

export default InvestorForm;
